import CachedIcon from '@mui/icons-material/Cached';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isValid } from 'date-fns';
import { useMemo, useRef, useState } from 'react';

import HealthRings, { HealthRingsDataPoint } from '@/components/common/HealthRings';
import RadarChart, { RadarChartDataPoint, RadarChartDataSeries } from '@/components/common/RadarChart';
import {
  COLORS,
  COND_RADAR_TOOLTIPS,
  ScoreLockup,
  LoadingRadarChart,
  MobileCategoryBars,
  SCORE_TOOLTIPS,
  ShimmerValue,
  Tip,
} from '@/components/dashboard/scoring';
import {
  ManualPricePromptDialog,
  enterListingPriceSx,
} from '@/components/property/ManualPricePromptDialog';
import { CardMode } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useUploaderContext } from '@/context/UploaderProvider';
import { useTriggerRecalculation } from '@/hooks/admin/useAdminProperties';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useGetIssuesForProperty } from '@/hooks/issues/useGetIssuesForProperty';
import { useDocumentRecords } from '@/hooks/useDocumentRecords';
import { useScoringConfig } from '@/hooks/useScoringConfig';
import { useUserRole } from '@/hooks/useUserRole';
import {
  DocumentClassificationType,
  DOCUMENT_TYPE_CONFIG,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_TYPE_SORT_ORDER,
} from '@/models/DocumentRecord';
import { useTranslationPrefix } from '@/translations/i18n';
import { parseDate } from '@/utils/formatDate';

export interface HomeScoreCardProps {
  cardMode?: CardMode;
  /** The Home on a computer: axis labels carry their scores, standing in for the category rows */
  axisValues?: boolean;
  footer?: React.ReactNode;
  /** 27": with a footer, fill the height it's given; the footer (rows) scrolls inside the card. */
  fill?: boolean;
  repairPreviewActive?: boolean;
  repairPreviewScore?: number | null;
  repairPreviewCategoryScores?: Partial<Record<string, number>>;
}

// Spider graph display order (clockwise from top):
// top=Structural, top-right=Exterior, bottom-right=Interior,
// bottom=HVAC, bottom-left=Plumbing, top-left=Electrical
// Exported so other condition-score layouts (e.g. ConditionScoreView's
// horizontal panel) can reorder radar data identically without duplicating
// this list out of sync.
export const CONDITION_RADAR_ORDER = [
  'structural',
  'exterior',
  'interior',
  'hvac',
  'plumbing',
  'electrical',
];
export const CONDITION_LABEL_ALIASES: Record<string, string> = {
  hvacsystems: 'hvac',
  'hvac&systems': 'hvac',
};
export const CONDITION_CATEGORIES = [
  'STRUCTURAL',
  'EXTERIOR',
  'INTERIOR',
  'HVAC',
  'PLUMBING',
  'ELECTRICAL',
];

// Per-category radar segment color, by score value — shared so other
// previews of the same condition-score radar (e.g. the onboarding reveal)
// use identical hues rather than an approximated palette.
export const scoreToColor = (s: number): string =>
  s >= 86
    ? '#f3f3f3'
    : s >= 77
    ? '#5AE9FF'
    : s >= 68
    ? COLORS.moderate
    : s >= 59
    ? COLORS.urgent
    : COLORS.critical;

// Radar keys with no inspection findings — rendered as N/A (dashed) axes
// rather than a numeric score. Extracted so other consumers of the
// condition radar (e.g. the onboarding reveal) mark the same axes N/A
// instead of plotting a misleading value for an uninspected category.
export const computeNaRadarKeys = (
  issues: { category: string }[] | undefined,
): Set<string> => {
  if (!issues?.length) return new Set<string>();
  const RADAR_KEY_TO_CAT: Record<string, string> = {
    structural: 'STRUCTURAL',
    exterior: 'EXTERIOR',
    interior: 'INTERIOR',
    hvac: 'HVAC_SYSTEMS',
    plumbing: 'PLUMBING',
    electrical: 'ELECTRICAL',
  };
  const withIssues = new Set(
    issues.map((issue) => {
      const upper = issue.category.toUpperCase().replace(/\s+/g, '_');
      return upper === 'HVAC' ? 'HVAC_SYSTEMS' : upper;
    }),
  );
  return new Set(CONDITION_RADAR_ORDER.filter((key) => !withIssues.has(RADAR_KEY_TO_CAT[key] ?? '')));
};

// Reorders raw per-category scores into the fixed clockwise-from-top layout
// (CONDITION_RADAR_ORDER) and stamps isNa flags — the exact transform
// HomeScoreCard's own radar uses, extracted so any other preview of "this
// home's condition radar" is guaranteed to plot identically, not an
// approximation of it.
export const reorderConditionRadarData = (
  conditionRadarData: RadarChartDataPoint[] | null | undefined,
  naRadarKeys: Set<string>,
): RadarChartDataPoint[] | null | undefined => {
  if (!conditionRadarData || conditionRadarData.length !== 6) return conditionRadarData;
  const norm = (s: string) => s.toLowerCase().replace(/[\s_]+/g, '');
  const byKey = Object.fromEntries(
    conditionRadarData.map((d) => {
      const k = norm(d.label);
      return [CONDITION_LABEL_ALIASES[k] ?? k, d];
    }),
  );
  return CONDITION_RADAR_ORDER.map((key) => {
    const point = byKey[key] ?? conditionRadarData[0];
    return { ...point, isNa: naRadarKeys.has(key) };
  });
};

// The real condition radar series for a property — same reordering, N/A
// handling, and per-category hues as the dashboard's own radar (no repair-
// preview overlay support, since that's only meaningful on the interactive
// dashboard card). Use this wherever "this home's actual condition radar"
// needs to render elsewhere, so it stays one-for-one with the dashboard
// instead of drifting into its own approximation.
export const useConditionRadarSeries = (propertyId?: string): RadarChartDataSeries[] | undefined => {
  const { conditionRadarData } = useHauserEQResults(propertyId);
  const { issues } = useGetIssuesForProperty(propertyId ?? '');

  return useMemo(() => {
    const naRadarKeys = computeNaRadarKeys(issues);
    const radarData = reorderConditionRadarData(conditionRadarData, naRadarKeys);
    if (!radarData) return undefined;
    return [
      {
        data: radarData,
        fillColor: `${COLORS.urgent}25`,
        segmentColors: radarData.map((point) => scoreToColor(point.value)),
        strokeWidth: 3,
      },
    ];
  }, [conditionRadarData, issues]);
};

type VizMode = 'bars' | 'spider' | 'rings';

const MOBILE_VIZ_SIZE = 245 * 1.3;
const MOBILE_RINGS_SIZE = MOBILE_VIZ_SIZE * 0.9;

const HomeScoreCard = ({
  cardMode: _cardMode = 'light',
  footer,
  fill = false,
  axisValues = false,
  repairPreviewActive = false,
  repairPreviewScore = null,
  repairPreviewCategoryScores,
}: HomeScoreCardProps) => {
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [vizMode, setVizMode] = useState<VizMode>('spider');
  const { selectedProperty } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const { isAdmin } = useUserRole();
  const recalcMutation = useTriggerRecalculation();
  const tooltipContainerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { showAlert } = useSnackbar();
  const t = useTranslationPrefix('toasts');

  const cardBgColor = COLORS.card;

  const {
    conditionRadarData,
    conditionScore,
    conditionTabs: _conditionTabs,
    isLoading,
    isProcessing,
    isConditionProcessing,
    isNoListPrice,
    conditionScoredAt,
  } = useHauserEQResults(propertyId);
  const { stepProcessing } = useUploaderContext();
  const { scoreToGrade } = useScoringConfig();
  const { issues } = useGetIssuesForProperty(propertyId ?? '');
  const { documentRecords } = useDocumentRecords(propertyId);
  const processedDocs = (documentRecords ?? [])
    .filter((r) => r.documentType && DOCUMENT_TYPE_CONFIG[r.documentType]?.group === 'inspection')
    .sort((a, b) => {
      const ai = DOCUMENT_TYPE_SORT_ORDER.indexOf(a.documentType as DocumentClassificationType);
      const bi = DOCUMENT_TYPE_SORT_ORDER.indexOf(b.documentType as DocumentClassificationType);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

  const score = conditionScore;
  const displayScore =
    repairPreviewActive && typeof repairPreviewScore === 'number' ? repairPreviewScore : score;

  // Radar keys that have no inspection findings — used to render N/A axes
  const naRadarKeys = useMemo(() => computeNaRadarKeys(issues), [issues]);

  // Reorder radar data to match desired spider graph layout by label
  const radarData = useMemo(
    () => reorderConditionRadarData(conditionRadarData, naRadarKeys),
    [conditionRadarData, naRadarKeys],
  );
  const previewRadarData = useMemo(() => {
    if (!radarData || !repairPreviewActive || !repairPreviewCategoryScores) return radarData;
    return radarData.map((point, index) => {
      const key = CONDITION_RADAR_ORDER[index];
      const previewValue = key ? repairPreviewCategoryScores[key] : undefined;
      return { ...point, value: previewValue ?? point.value };
    });
  }, [radarData, repairPreviewActive, repairPreviewCategoryScores]);
  const hasData = score != null;
  const grade = displayScore != null ? scoreToGrade(displayScore) : null;

  const series: RadarChartDataSeries[] = useMemo(() => {
    if (!radarData) return [];
    const segmentColors = radarData.map((point) => scoreToColor(point.value));
    const currentSeries: RadarChartDataSeries = {
      data: radarData,
      fillColor: repairPreviewActive ? 'rgba(187,183,175,0.06)' : `${COLORS.urgent}25`,
      strokeColor: repairPreviewActive ? 'rgba(187,183,175,0.34)' : undefined,
      strokeWidth: 3,
      segmentColors: repairPreviewActive ? undefined : segmentColors,
    };
    if (!repairPreviewActive || !repairPreviewCategoryScores || displayScore == null) {
      return [currentSeries];
    }

    const previewData = previewRadarData ?? radarData;
    const previewSegmentColors = previewData.map((point) => scoreToColor(point.value));

    return [
      currentSeries,
      {
        data: previewData,
        fromData: radarData,
        fillColor: `${COLORS.urgent}25`,
        segmentColors: previewSegmentColors,
        strokeWidth: 3,
        animatePathMs: 650,
      },
    ];
  }, [displayScore, previewRadarData, radarData, repairPreviewActive, repairPreviewCategoryScores]);

  const healthRingsData: HealthRingsDataPoint[] = useMemo(() => {
    const visualData = repairPreviewActive ? previewRadarData : radarData;
    if (!visualData) return [];
    return visualData.map((point, i) => ({
      label: CONDITION_CATEGORIES[i] ?? point.label,
      value: point.value,
      color: scoreToColor(point.value),
      isNa: point.isNa,
    }));
  }, [previewRadarData, radarData, repairPreviewActive]);

  // Build mobile category bar data from radar data
  const mobileCategoryData = useMemo(() => {
    const visualData = repairPreviewActive ? previewRadarData : radarData;
    if (!visualData) return [];
    const data = visualData.map((point, i) => ({
      label: CONDITION_CATEGORIES[i] ?? point.label,
      score: point.isNa ? null : Math.round(point.value),
    }));
    // Swap PLUMBING (index 3) and HVAC (index 4)
    if (data.length >= 5) {
      [data[3], data[4]] = [data[4], data[3]];
    }
    return data;
  }, [previewRadarData, radarData, repairPreviewActive]);

  if (!propertyId || isLoading) {
    return (
      <Box sx={{ height: footer ? 'auto' : '100%' }}>
        <Card
          sx={{
            bgcolor: cardBgColor,
            borderRadius: '24px',
            minHeight: isMobile || !footer ? 'auto' : 660,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <CardContent sx={{ p: '20px', display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Stack spacing={2} sx={{ flex: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <span style={{ fontSize: 17, fontWeight: 600, color: COLORS.text, opacity: 0.5 }}>
                  The Home
                </span>
                <div style={{ textAlign: 'right' }}>
                  <ShimmerValue width={72} height={60} borderRadius={8} />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 8,
                      marginTop: 4,
                    }}>
                    <span style={{ fontSize: 17, color: COLORS.textDim, opacity: 0.4 }}>/100</span>
                    <ShimmerValue width={32} height={22} borderRadius={10} />
                  </div>
                </div>
              </Stack>
              {!isMobile && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                  }}>
                  <LoadingRadarChart
                    labels={[
                      'Structural',
                      'Exterior',
                      'Interior',
                      'Plumbing',
                      'HVAC',
                      'Electrical',
                    ]}
                    accentColor={COLORS.urgent}
                    size={550}
                  />
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const propertyHasPrice = !!selectedProperty?.property.price;
  const isReportProcessing =
    isProcessing || isConditionProcessing || (stepProcessing >= 1 && stepProcessing < 5);

  if (!isLoading && !hasData && isReportProcessing) {
    return (
      <Box>
        <Card
          sx={{
            bgcolor: cardBgColor,
            borderRadius: '24px',
            minHeight: isMobile ? 'auto' : 660,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <CardContent
            sx={{
              p: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: isMobile ? 200 : 620,
              flex: 1,
            }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16,
              }}>
              <span style={{ fontSize: 24, fontWeight: 600, color: '#494949' }}>The Home</span>
              <CircularProgress size={40} thickness={3} sx={{ color: '#C0FF02' }} />
              <span style={{ fontSize: 14, color: 'var(--tx-muted)', textAlign: 'center' }}>
                Inspection report uploaded — processing
              </span>
              <span style={{ fontSize: 12, color: '#666', textAlign: 'center' }}>
                Your score will appear here shortly.
              </span>
            </div>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!isLoading && isNoListPrice) {
    // If the property already has a price saved in Blueprint (e.g. a manual override that was
    // entered previously), hauser-eq is just lagging on recalculation — don't re-prompt the
    // advisor to enter a price they already submitted.
    if (propertyHasPrice) {
      return (
        <Box>
          <Card
            sx={{
              bgcolor: cardBgColor,
              borderRadius: '24px',
              minHeight: isMobile ? 'auto' : 660,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
            <CardContent
              sx={{
                p: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: isMobile ? 200 : 620,
                gap: 2,
                flex: 1,
              }}>
              <span style={{ fontSize: 24, fontWeight: 600, color: '#494949' }}>The Home</span>
              <CircularProgress size={40} thickness={3} sx={{ color: '#C0FF02' }} />
              <span style={{ fontSize: 14, color: 'var(--tx-muted)', textAlign: 'center' }}>
                Recalculating condition score…
              </span>
              <span style={{ fontSize: 12, color: '#666', textAlign: 'center' }}>
                Your score will appear here shortly.
              </span>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return (
      <>
        <Box>
          <Card
            sx={{
              bgcolor: cardBgColor,
              borderRadius: '24px',
              minHeight: isMobile ? 'auto' : 660,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
            <CardContent
              sx={{
                p: '20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: isMobile ? 200 : 620,
                gap: 2,
                flex: 1,
              }}>
              <span style={{ fontSize: 24, fontWeight: 600, color: '#494949' }}>The Home</span>
              <span style={{ fontSize: 14, color: 'var(--tx-muted)', textAlign: 'center', maxWidth: 300 }}>
                A listing price is required to calculate this score.
              </span>
              <Button
                variant="contained"
                onClick={() => setPriceDialogOpen(true)}
                sx={enterListingPriceSx}>
                Enter listing price
              </Button>
            </CardContent>
          </Card>
        </Box>
        {selectedProperty && (
          <ManualPricePromptDialog
            open={priceDialogOpen}
            property={selectedProperty.property}
            onClose={() => setPriceDialogOpen(false)}
            onSaved={() => {
              setPriceDialogOpen(false);
              queryClient.invalidateQueries({ queryKey: ['properties'] });
              queryClient.invalidateQueries({ queryKey: ['hauser-eq-results', propertyId] });
              showAlert(t('price.recalculating'), 'info');
            }}
          />
        )}
      </>
    );
  }

  if (!isLoading && !hasData) {
    return (
      <Box>
        <Card
          sx={{
            bgcolor: cardBgColor,
            borderRadius: '24px',
            minHeight: isMobile ? 'auto' : 660,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <CardContent
            sx={{
              p: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: isMobile ? 200 : 620,
              flex: 1,
            }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <span style={{ fontSize: 24, fontWeight: 600, color: '#494949', marginBottom: 10 }}>
                The Home
              </span>
              <span style={{ fontSize: 14, color: '#494949' }}>Data not available</span>
            </div>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    // a stable view-transition name: moving between Overview and this card's own page, the card glides into place
    <Box data-testid="condition-score-card" sx={{ height: footer && !fill ? 'auto' : '100%', viewTransitionName: 'hx-home-card' }}>
      <Card
        sx={{
          bgcolor: cardBgColor,
          borderRadius: '24px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <CardContent
          ref={tooltipContainerRef}
          sx={{
            p: '20px',
            '&:last-child': { pb: footer ? '0px' : '20px' },
            display: 'flex',
            flexDirection: 'column',
            minHeight: footer && !fill ? 'auto' : isMobile ? 'auto' : 0,
            position: 'relative',
            flex: 1,
          }}>
          <Stack spacing={2} sx={{ flex: 1, minHeight: fill ? 0 : undefined }}>
            {/* Header: Title (left) + Score + /100 + Grade (right) */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{ position: 'relative', zIndex: 3, pointerEvents: 'none' }}>
              {/* 27" (fill): the title block reserves the same height as The Area's, so both radars sit at the same y */}
              <div style={{ pointerEvents: 'auto', minHeight: fill ? 72 : undefined }}>
                <Tip text={SCORE_TOOLTIPS.ConditionScore}>
                  <span
                    style={{ fontSize: 22, fontWeight: 300, color: COLORS.text, cursor: 'help' }}>
                    The Home
                  </span>
                </Tip>
                {conditionScoredAt && isValid(parseDate(conditionScoredAt)) && (
                  <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
                    Last scored{' '}
                    {parseDate(conditionScoredAt).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </div>
                )}
                {processedDocs.length > 0 && footer && !isMobile && (
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: 'var(--tx-faint)',
                      marginTop: 4,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 'min(520px, 60vw)',
                    }}>
                    Sources ·{' '}
                    {processedDocs
                      .map((r) => DOCUMENT_TYPE_LABELS[r.documentType as DocumentClassificationType] ?? r.documentType)
                      .join(' · ')}
                  </div>
                )}
                {processedDocs.length > 0 && isMobile && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                    {processedDocs.map((r) => (
                      <span
                        key={r.id}
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: COLORS.textDim,
                          background: 'var(--tx-fill-2)',
                          border: 0,
                          borderRadius: 6,
                          padding: '2px 7px',
                          letterSpacing: '0.02em',
                        }}>
                        {DOCUMENT_TYPE_LABELS[r.documentType as DocumentClassificationType] ??
                          r.documentType}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', pointerEvents: 'auto' }}>
                {isAdmin && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
                    <Tooltip title="Re-run condition score" placement="top">
                      <span>
                        <IconButton
                          size="small"
                          disabled={recalcMutation.isPending}
                          aria-label="Re-run condition score"
                          onClick={() =>
                            propertyId && recalcMutation.mutate({ propertyId, scoreType: 'home' })
                          }
                          sx={{ color: 'text.secondary2', p: 0.5, ml: 0.5 }}>
                          {recalcMutation.isPending ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : (
                            <CachedIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                )}
                <ScoreLockup score={displayScore as number} grade={grade as string} color={COLORS.text} compact={isMobile} />
                {
                  // Always mounted (reserves its space) so the header is the same
                  // height whether or not footer is present — otherwise the chart
                  // box below sits at a different vertical position depending on
                  // which page rendered this card.
                  <div
                    style={{
                      justifyContent: 'flex-end',
                      marginTop: 10,
                      // Overview has no chart toggle and keeps no room for one
                      display: footer || isMobile ? 'flex' : 'none',
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        background: 'var(--tx-fill)',
                        borderRadius: 10,
                        padding: 3,
                        gap: 2,
                      }}>
                      {(isMobile
                        ? (['spider', 'bars', 'rings'] as const)
                        : (['spider', 'rings'] as const)
                      ).map((mode) => {
                        const active = vizMode === mode;
                        let icon: React.ReactNode;
                        if (mode === 'bars') {
                          const c1 = active ? '#5AE9FF' : 'rgba(255,255,255,0.35)';
                          const c2 = active ? '#FFF981' : 'rgba(255,255,255,0.35)';
                          const c3 = active ? '#DB5282' : 'rgba(255,255,255,0.35)';
                          icon = (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <rect x="1.5" y="7" width="2.4" height="5.5" rx="0.6" fill={c1} />
                              <rect x="5.8" y="3.5" width="2.4" height="9" rx="0.6" fill={c2} />
                              <rect x="10.1" y="5.5" width="2.4" height="7" rx="0.6" fill={c3} />
                            </svg>
                          );
                        } else if (mode === 'spider') {
                          const outerC = active ? '#5AE9FF' : 'rgba(255,255,255,0.35)';
                          const middleC = active ? '#FFF981' : 'rgba(255,255,255,0.35)';
                          const innerC = active ? '#FF9A4D' : 'rgba(255,255,255,0.35)';
                          icon = (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <polygon
                                points="7,1.5 11.8,4.25 11.8,9.75 7,12.5 2.2,9.75 2.2,4.25"
                                stroke={outerC}
                                strokeWidth="0.9"
                                fill="none"
                              />
                              <polygon
                                points="7,3.8 10,5.5 10,8.5 7,10.2 4,8.5 4,5.5"
                                stroke={middleC}
                                strokeWidth="0.9"
                                fill="none"
                              />
                              <polygon
                                points="7,5.5 8.6,6.4 8.6,7.6 7,8.5 5.4,7.6 5.4,6.4"
                                stroke={innerC}
                                strokeWidth="0.9"
                                fill="none"
                              />
                            </svg>
                          );
                        } else {
                          const outerC = active ? '#5AE9FF' : 'rgba(255,255,255,0.35)';
                          const middleC = active ? '#FFF981' : 'rgba(255,255,255,0.35)';
                          const innerC = active ? '#DB5282' : 'rgba(255,255,255,0.35)';
                          icon = (
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                              <circle cx="7" cy="7" r="5.5" stroke={outerC} strokeWidth="1" />
                              <circle cx="7" cy="7" r="3.3" stroke={middleC} strokeWidth="1" />
                              <circle cx="7" cy="7" r="1.2" stroke={innerC} strokeWidth="1" />
                            </svg>
                          );
                        }
                        return (
                          <div
                            key={mode}
                            onClick={() => setVizMode(mode)}
                            style={{
                              padding: 5,
                              borderRadius: 7,
                              cursor: 'pointer',
                              background: active ? 'var(--tx-fill-3)' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'background 0.15s',
                              userSelect: 'none',
                            }}>
                            {icon}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                }
              </div>
            </Stack>

            {/* Mobile: toggleable bars/radar/rings | Desktop: Radar Chart / Health Rings */}
            {isMobile ? (
              // Mobile score card — toggle between bars / radar / rings. Fixed height (not
              // minHeight) matching radar's own box size, and rings shrunk to the same box
              // size, so the container never resizes when switching visualizations.
              // mx cancels CardContent's 20px padding (same full-bleed trick the footer below uses).
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  height: MOBILE_VIZ_SIZE,
                  mx: '-20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                {vizMode === 'bars' && (
                  <Box sx={{ width: '100%', px: '20px' }}>
                    <MobileCategoryBars categories={mobileCategoryData} />
                  </Box>
                )}
                {vizMode === 'spider' && (
                  // RadarChart widens its radius fraction to 0.46 when showLabels is false,
                  // reclaiming the label margin, so this fills its 245px box.
                  // translateY compensates RadarChart's own asymmetric -45px/-10px vertical
                  // margins, which otherwise pull its visual center 17.5px above HealthRings'.
                  <Box sx={{ transform: 'translateY(17.5px)' }}>
                    <RadarChart
                      series={series}
                      size={MOBILE_VIZ_SIZE}
                      gridColor="rgba(255,255,255,0.1)"
                      labelColor={COLORS.textDim}
                      axisLabelColor="#BBB7AF"
                      gridLevels={4}
                      showLabels={false}
                      showValueTooltips
                      labelTooltips={COND_RADAR_TOOLTIPS}
                      animateOnDataChange={!repairPreviewActive}
                      tooltipContainerRef={tooltipContainerRef}
                    />
                  </Box>
                )}
                {vizMode === 'rings' && (
                  // Slightly smaller than the radar within the fixed visualization footprint.
                  <HealthRings data={healthRingsData} size={MOBILE_RINGS_SIZE} />
                )}
              </Box>
            ) : footer ? (
              // Fixed-height container with both charts overlaid — footer stays locked
              <Box sx={{ position: 'relative', zIndex: 2, height: fill ? undefined : 420, minHeight: fill ? 420 : undefined, flex: fill ? 1 : undefined }}>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    opacity: vizMode === 'spider' ? 1 : 0,
                    pointerEvents: vizMode === 'spider' ? 'auto' : 'none',
                    transition: 'opacity 0.2s',
                  }}>
                  <RadarChart
                    series={series}
                    size={550}
                    radiusFraction={0.37}
                    contoured
                    gridColor="rgba(255,255,255,0.1)"
                    labelColor={COLORS.textDim}
                    axisLabelColor="#BBB7AF"
                    gridLevels={4}
                    labelTooltips={COND_RADAR_TOOLTIPS}
                    animateOnDataChange={!repairPreviewActive}
                    tooltipContainerRef={tooltipContainerRef}
                  />
                </Box>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    opacity: vizMode === 'rings' ? 1 : 0,
                    pointerEvents: vizMode === 'rings' ? 'auto' : 'none',
                    transition: 'opacity 0.2s',
                  }}>
                  <HealthRings data={healthRingsData} size={365} />
                </Box>
              </Box>
            ) : (
              // No footer (Overview): the chart takes all the height the card is given and the radar fills it, sized by
              // whichever of the box's width or height runs out first (container units). The radar's own -45px top margin
              // lets it rise into the header's empty right side, so it may be 55px taller than the box.
              <Box sx={{ flex: 1, minHeight: 0, position: 'relative', zIndex: 2, containerType: 'size' }}>
                <Box
                  sx={{
                    width: 'min(100cqw, calc(100cqh + 55px))',
                    mx: 'auto',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                  <RadarChart
                    series={series}
                    size={640}
                    radiusFraction={axisValues ? 0.33 : 0.37}
                    axisValues={axisValues}
                    contoured
                    gridColor="rgba(255,255,255,0.1)"
                    labelColor={COLORS.textDim}
                    axisLabelColor="#BBB7AF"
                    gridLevels={4}
                    labelTooltips={COND_RADAR_TOOLTIPS}
                    animateOnDataChange={!repairPreviewActive}
                    tooltipContainerRef={tooltipContainerRef}
                  />
                </Box>
              </Box>
            )}
          </Stack>

          {/* Overview: the reports behind the score as one quiet line in the bottom-left corner */}
          {!footer && !isMobile && processedDocs.length > 0 && (
            <div
              data-testid="condition-score-sources"
              style={{
                position: 'absolute',
                left: 20,
                bottom: 16,
                zIndex: 3,
                fontSize: 11,
                fontWeight: 500,
                color: 'var(--tx-faint)',
              }}>
              Sources ·{' '}
              {processedDocs
                .map((r) => DOCUMENT_TYPE_LABELS[r.documentType as DocumentClassificationType] ?? r.documentType)
                .join(' · ')}
            </div>
          )}

          {footer && (
            <Box
              sx={{
                mx: '-20px',
                mt: isMobile ? '-22px' : 1,
                ...(fill && {
                  flex: 'none',
                  maxHeight: '45%',
                  overflowY: 'auto',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }),
              }}>
              {footer}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default HomeScoreCard;
